import * as React from 'react'
import { DataGrid } from '@mui/x-data-grid'
import {
  Box,
  Button,
  Chip,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { BackArrowIcon } from '../../icons'
import { GlobalStyles } from '@mui/material'
import UniversitySummary from '../university-table/UniversitySummary'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import UserCommentModal from './CommentModal/UserCommentModal'
import ActionDropdown from './ActionDropdown'

import { fetchSpecializationList, fetchSpecializationsNameSuggestion } from '../../api/specializationApi'
import { updateStatus } from '../../api/adminApi'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { setPaginationFiled } from '../../containers/pagination-container/reducer/paginationSlice'
import CommonFilterTab from '../common/CommonFilterTab'
import CommonTableFormat from '../common/CommonTableFormat'
import { setFilterField } from '../../containers/filter-container/reducer/filterSlice'
import { defaultPaginationValues, filterTypeOptions, studentStatusList } from '../../utils/constant'
import { formatMultiQueryParams, jsonParserFormatter } from '../../utils/helperFunction'
import { fetchUniversitiesNames } from '../../api/universityApi'
import { fetchCourseNameList } from '../../api/courseApi'
import CommonDataTable from '../common/CommonDataTable'

const SpecializationReview = ({ isHeadHide = true, activeTab = 0, type, module }) => {
  const filterSlice = useSelector(state => state.filter?.filters)
  const LOCATION_FILTER = `filters_${activeTab}:${window.location.pathname}`
  const dispatch = useDispatch()
  const [rows, setRows] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchText, setSearchText] = React.useState('')
  const [selectedStatus, setSelectedStatus] = React.useState('')
  const [openCommentModal, setOpenCommentModal] = React.useState(false)
  const [commentText, setCommentText] = React.useState('')
  const [selectedRowId, setSelectedRowId] = React.useState(null)
  const [rowCount, setRowCount] = React.useState(0);
  const [searchFilter, setSearchFilter] = React.useState({})
  const [pageFilters, setPageFilters] = React.useState(defaultPaginationValues);
  const [isRender, setIsRender] = React.useState(false)
  const [universitySuggestion, setUniversitySuggesion] = React.useState([])
  const [courseSuggestion, setCourseSuggestion] = React.useState([])
  const [specializationSuggestion, setSpecializationSuggestion] = React.useState([])

  const handleFliterSliceChange = (values = {}, isReset = false) => {
    let isQuery = typeof values === "object" && Object.keys(values)?.length
    isQuery && dispatch(setFilterField({ pathname: LOCATION_FILTER, filters: JSON.stringify({ ...(isReset ? {} : pageFilters), ...defaultPaginationValues, ...values }) }))
  }

  const handleSaveComment = () => {
    handleChangeStatus(selectedRowId, 'specialization', 'Rework', commentText)
    setOpenCommentModal(false)
    setCommentText('')
  }

  React.useEffect(() => {
    setLoading(true)
    const multiFilter = formatMultiQueryParams(filterSlice[LOCATION_FILTER])
    fetchSpecializationList(setRows, { ...searchFilter, ...multiFilter, isMultiFilter: true }, false, setRowCount).finally(() => setLoading(false))
  }, [filterSlice[LOCATION_FILTER], isRender])

  // handle extra filter tabs changes
  React.useEffect(() => {
    if (pageFilters?.page) {
      handleFliterSliceChange({ page: 0 })
    } else {
      setIsRender(!isRender)
    }
  }, [searchFilter])

  // parse page filters
  React.useEffect(() => {
    const values = jsonParserFormatter(filterSlice[LOCATION_FILTER])
    setPageFilters(values)
  }, [filterSlice[LOCATION_FILTER]])

  // load course/ universities for filter suggestions
  React.useEffect(() => {
    fetchUniversitiesNames((result) => setUniversitySuggesion(result?.map(item => ({ ...item, value: item?.label }))))
      .catch(err => console.log("error:---", err))

    fetchCourseNameList((result) => { setCourseSuggestion(result?.map(el => ({ label: el?.course, value: el?.course }))) }, { skipLimit: true })
      .catch(() => { })

    fetchSpecializationsNameSuggestion((result) => { setSpecializationSuggestion(result?.map(el => ({ ...el, value: el?.label }))) }, { skipLimit: true })
      .catch(() => { })
  }, [])

  const handleChangeStatus = async (rowId, type, action, reason = '', event) => {
    if (action === 'approve' || action === 'Approve') {
      try {
        await updateStatus({ id: rowId, status: 'approved', type: 'specialization' })
        setIsRender(!isRender)
      } catch (err) {
        console.log('err', err)
      }
    }
    if (action === 'Rework') {
      try {
        await updateStatus({ id: rowId, status: 'rework', reason, type: 'specialization' })
        setIsRender(!isRender)
      } catch (err) {
        console.log('err', err)
      }
    }
    if (action === 'View') {
      const specializationId = rows.find(r => r.id === rowId)?.specId || '';
      const url = `/specialization-listing-management/specialization-details/${specializationId}`
      if (event?.ctrlKey || event?.metaKey) {
        window.open(url, '_blank')
      } else {
        navigate(url, {
          state: { viewOnly: true, source: 'ActionMenu', backUrl: location?.pathname, activeTab: activeTab }
        });
      }
      return;
    }
  }

  function getStatusClass(status) {
    switch (status) {
      case 'Pending':
        return 'status-pending'
      case 'Approved':
        return 'status-live'
      case 'Re-Sumbited':
        return 'status-draft'
      case 'Send for Rework':
        return 'status-draft'
      case 'Draft':
        return 'status-deactivated'
      default:
        return ''
    }
  }

  const columns = [
    {
      field: 'sNo',
      headerName: 'S. No.',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      // renderCell: (params) => rows?.findIndex((r) => r.id === params.row.id) + 1,
    },
    {
      field: 'specId',
      headerName: 'Spec. ID',
      flex: 1,
      minWidth: 130,
      cellClassName: 'sticky-col-1',
      headerClassName: 'sticky-col-1',
      filterColumnName: "specialisationId",
      filterType: filterTypeOptions.string,
    },
    {
      field: 'name',
      headerName: 'Specialization Name',
      flex: 2,
      minWidth: 280,
      filterColumnName: "specialization",
      filterType: filterTypeOptions.selection,
      selection: specializationSuggestion,
      renderCell: (params) => {
        const { img, name } = params.row

        const getInitials = (fullName) => {
          if (!fullName) return ''
          const words = fullName.trim().split(' ').filter(Boolean)
          if (words.length === 1) return words[0][0]?.toUpperCase() || ''
          if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase()
          return (words[0][0] + words[1][0] + words[2][0]).toUpperCase()
        }

        const stringToColor = (str) => {
          let hash = 0
          for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash)
          }
          const c = (hash & 0x00ffffff).toString(16).toUpperCase()
          return '#' + '00000'.substring(0, 6 - c.length) + c
        }

        const bgColor = stringToColor(name || 'Spec')

        return loading ? (
          <Skeleton width={150} />
        ) : (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/specialization-listing-management/parents-specialization')}
          >
            {img ? (
              <Box
                component="img"
                src={img}
                alt="logo"
                sx={{
                  width: 40,
                  height: 40,
                  padding: '5px',
                  borderRadius: '50%',
                  objectFit: 'contain',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                  display: 'block',
                  maxHeight: 60,
                }}
              />
            ) : (
              <Box
                sx={{
                  minWidth: 35,
                  minHeight: 35,
                  maxWidth: 35,
                  maxHeight: 35,
                  borderRadius: '50%',
                  backgroundColor: bgColor,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 500,
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                {getInitials(name)}
              </Box>
            )}
            {params.value}
          </Box>
        )
      },
    },
    {
      field: 'courseName',
      headerName: 'Course Name',
      flex: 1,
      minWidth: 160,
      filterColumnName: "course",
      filterType: filterTypeOptions.selection,
      selection: courseSuggestion,
    },
    {
      field: 'university',
      headerName: 'University',
      flex: 2,
      minWidth: 260,
      filterColumnName: "universityName",
      filterType: filterTypeOptions.selection,
      selection: universitySuggestion,
      renderCell: (params) => {
        const { universityLogo, university, universityId } = params.row
        const displayName = university

        const getInitials = (fullName) => {
          if (!fullName) return ''
          const words = fullName.trim().split(' ').filter(Boolean)
          if (words.length === 1) return words[0][0]?.toUpperCase() || ''
          if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase()
          return (words[0][0] + words[1][0] + words[2][0]).toUpperCase()
        }

        const stringToColor = (str) => {
          let hash = 0
          for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash)
          }
          const c = (hash & 0x00ffffff).toString(16).toUpperCase()
          return '#' + '00000'.substring(0, 6 - c.length) + c
        }

        const bgColor = stringToColor(displayName || 'University')

        return loading ? (
          <Skeleton width={150} />
        ) : (
          <Box display="flex" alignItems="center" gap={1} sx={{ height: '100%' }}>
            {universityLogo ? (
              <Box
                component="img"
                src={universityLogo}
                alt="logo"
                sx={{
                  width: 40,
                  height: 40,
                  padding: '5px',
                  borderRadius: '50%',
                  objectFit: 'contain',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                  display: 'block',
                  maxHeight: 60,
                }}
              />
            ) : (
              <Box
                sx={{
                  minWidth: 35,
                  minHeight: 35,
                  maxWidth: 35,
                  maxHeight: 35,
                  borderRadius: '50%',
                  backgroundColor: bgColor,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 500,
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                {getInitials(displayName)}
              </Box>
            )}
            <Box>
              <Typography sx={{ fontSize: '13px !important', fontWeight: 500 }}>
                {displayName}
              </Typography>
              {params.row.universityNote && (
                <Typography sx={{ fontSize: '11px !important', fontWeight: '500', color: '#363853' }}>
                  ({params.row.universityNote})
                </Typography>
              )}
              <Typography
                sx={{ fontSize: '11px !important', fontWeight: '500', color: '#6E7485', paddingTop: '4px' }}
              >
                University ID: {universityId}
              </Typography>
            </Box>
          </Box>
        )
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 130,
      filterColumnName: "status",
      filterType: filterTypeOptions.selection,
      selection: studentStatusList,
      renderCell: (params) =>
        loading ? (
          <Skeleton width={70} />
        ) : (
          <Chip
            label={params.value}
            size="small"
            icon={
              <FiberManualRecordIcon
                className={getStatusClass(params.value)}
                sx={{ fontSize: 10, color: getStatusColor(params.value) }}
              />
            }
            sx={{
              bgcolor: getStatusBgColor(params.value),
              color: getStatusTextColor(params.value),
              fontWeight: 500,
            }}
          />
        ),
    },
    {
      field: 'createdDate',
      headerName: 'Created Date',
      flex: 1,
      minWidth: 130,
      filterColumnName: "createdAt",
      filterType: filterTypeOptions.dateRange,
    },
    {
      field: 'lastUpdate',
      headerName: 'Last Update',
      flex: 1,
      minWidth: 130,
      filterColumnName: "updatedAt",
      filterType: filterTypeOptions.dateRange,
    },
    {
      field: 'completion',
      headerName: 'Completion',
      width: 100,
      filterColumnName: "completionPercentage",
      filterType: filterTypeOptions.numberRange,
      renderCell: (params) => (
        <Box
          sx={{
            color: params.value <= 41 ? '#FF4141' : params.value < 51 ? '#f59e0b' : '#22c55e',
            fontWeight: 500,
            fontSize: '13px',
          }}
        >
          {params.value}%
        </Box>
      ),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      width: 140,
      flex: 1,
      filterColumnName: "createdBy",
      filterType: filterTypeOptions.string,
      renderCell: (params) =>
        loading ? (
          <Skeleton width={80} />
        ) : (
          <Tooltip
            title={params.value || ''}
            placement="top"
            arrow
            PopperProps={{
              modifiers: [{ name: 'offset', options: { offset: [0, -20] } }],
            }}
          >
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'inline-block',
                maxWidth: '100%',
              }}
            >
              {params.value}
            </span>
          </Tooltip>
        ),
    },
    {
      field: 'reviewedBy',
      headerName: 'Reviewed By',
      flex: 1,
      minWidth: 130,
      filterColumnName: "reviewedBy",
      filterType: filterTypeOptions.string,
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      width: 140,
      filterable: false,
      renderCell: (params) =>
        loading ? (
          <Skeleton width={80} />
        ) : (
          <ActionDropdown
            rowId={params.row.id}
            onAction={handleChangeStatus}
            onRejectClick={() => {
              setSelectedRowId(params.row.id)
              setCommentText('')
              setOpenCommentModal(true)
            }}
            status={params?.row?.status}
          />
        ),
    },
  ]

  function getStatusBgColor(status) {
    switch (status) {
      case 'Live':
      case 'Draft':
      case 'Pending':
      case 'Deactivated':
      default:
        return '#fff'
    }
  }

  function getStatusTextColor(status) {
    switch (status) {
      case 'Live':
      case 'Draft':
      case 'Pending':
      case 'Deactivated':
        return '#363853'
      default:
        return '#666'
    }
  }

  function getStatusColor(status) {
    switch (status) {
      case 'Live':
        return '#22c55e'
      case 'Draft':
        return '#64748b'
      case 'Pending':
        return '#f97316'
      case 'Deactivated':
        return '#ef4444'
      default:
        return '#999'
    }
  }

  return (
    <>
      <GlobalStyles
        styles={{
          '.MuiDataGrid-menuList': { fontSize: '13px', padding: '4px 0' },
          '.MuiDataGrid-panelContent': { fontSize: '13px', padding: '4px 0' },
          '.MuiSelect-select': { fontSize: '13px', padding: '4px 0' },
          '.MuiList-root .MuiButtonBase-root': { fontSize: '13px' },
          '.MuiDataGrid-panelContent .MuiOutlinedInput-input': { fontSize: '13px' },
          '.MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label': { fontSize: '13px' },
          '.MuiDataGrid-menuList .MuiTypography-root': { fontSize: '13px' },
          '.MuiDataGrid-menuList .MuiMenuItem-root': { fontSize: '13px', padding: '6px 12px' },
          '.MuiDataGrid-menuList .MuiMenuItem-root:hover': { backgroundColor: '#f5f5f5' },
          '& .MuiDataGrid-columnHeaders': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff' },
          '& .MuiDataGrid-cell.sticky-col-1': { position: 'sticky', left: 0, background: '#fff', zIndex: 2 },
          '& .MuiDataGrid-columnHeader.sticky-col-1': { position: 'sticky', left: 0, background: '#fff', zIndex: 3 },
        }}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {isHeadHide && (
          <div className="page-header" style={{ position: 'sticky', top: '4.5rem', zIndex: '9', padding: '10px 15px', borderBottom: '1px solid #E6E7E8', background: '#fff' }}>
            <div className="row align-items-center">
              <div className="col">
                <Typography sx={{ fontSize: '18px', fontWeight: '600', color: '#15243E', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Link href="/" className="d-flex">
                    <BackArrowIcon />
                  </Link>
                  Specialization Listing Management
                </Typography>
              </div>
              <div className="col">
                <Box display="flex" gap={2} sx={{ justifyContent: 'end' }}>
                  <Button
                    onClick={() => navigate('/university-listing-management/university-details')}
                    sx={{ background: '#0463EF', color: '#fff', minHeight: 'auto', padding: '4px 15px', fontSize: '14px', textTransform: 'capitalize' }}
                  >
                    Add University
                  </Button>
                </Box>
              </div>
            </div>
          </div>
        )}

        <UniversitySummary type="specialization" isAdmin={false} isRender={isRender}/>

        {/* Filter Toolbar */}
        <CommonFilterTab type='specialization' module='reviewer' searchPlaceholder='Search specialization name...' detailType='Specialization Details' fetchList={setSearchFilter} showCount={rows?.length} rowCount={rowCount} isStatus={true} />

        {/* Data Table */}
        <CommonDataTable columns={columns} rows={rows} idKey="field" loading={loading} filterEnabled={true} rowCount={rowCount}
          pageFilters={pageFilters}
          handleFliterSliceChange={handleFliterSliceChange}
          otherPath={activeTab}
        />

      </Box>

      <UserCommentModal
        open={openCommentModal}
        onClose={() => setOpenCommentModal(false)}
        onSave={handleSaveComment}
        commentText={commentText}
        setCommentText={setCommentText}
      />
    </>
  )
}

export default SpecializationReview
